import { Box } from "@/components/ui/box";
import { FormControl, FormControlLabel, FormControlLabelText } from "@/components/ui/form-control";
import { Input, InputField } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { formatPhoneNumber, getPhoneDigits } from "@/utils/formatPhoneNumber";
import { useT } from "@transifex/react";
import { useFocusEffect } from "expo-router";
import { useCallback, useState } from "react";
import { Pressable } from "react-native";

type ContactInputProps = {
  onChange: (contact: string, isPhoneForm: boolean) => void;
  error: boolean;
  errorMessage: string;
  clearError?: () => void;
  initialValue: { phone: string; email: string };
};

const ContactInput: React.FC<ContactInputProps> = ({ onChange, error, errorMessage, clearError, initialValue }) => {
  const t = useT();
  // TODO: remove this. No sure why we are managing this state here and outside of it. But at this point fuck it.
  const [isEmailForm, setIsEmailForm] = useState(false);
  const [contact, setContact] = useState(initialValue.phone ? formatPhoneNumber(initialValue.phone) : initialValue.email);

  // Initial value is async and this mounts before it is available
  useFocusEffect(
    useCallback(() => {
      if (initialValue.phone || initialValue.email) {
        setIsEmailForm(!initialValue.phone);
        setContact(initialValue.phone ? formatPhoneNumber(initialValue.phone) : initialValue.email);
      }
    }, [initialValue]),
  );

  return (
    <FormControl isInvalid={error} key="contact-input">
      <Box className="gap-[6px]">
        <FormControlLabel className="flex items-center justify-between mb-0">
          <FormControlLabelText className="text-typography-900 text-[14px] font-[600] leading-[20px] align-middle">
            {isEmailForm ? t("Email Address") : t("Phone Number")}
          </FormControlLabelText>
          <Pressable
            onPress={() => {
              let value = "";
              if (isEmailForm) {
                value = initialValue.phone;
              } else {
                value = initialValue.email;
              }
              onChange(value, isEmailForm);
              setContact(isEmailForm ? formatPhoneNumber(value) : value);
              setIsEmailForm((prev) => !prev);
            }}
          >
            <Text className="text-typography-900 text-[14px] font-[400] leading-[20px] align-middle underline decoration-solid decoration-[0%] decoration-thickness-[0%]">
              {isEmailForm ? t("Phone Instead?") : t("Email Instead?")}
            </Text>
          </Pressable>
        </FormControlLabel>
        <Input
          className={`h-[52px] pt-[8px] pb-[8px] pl-[4px] gap-[8px] rounded-[6px] border-[1px] ${error ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
        >
          <InputField
            className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
            placeholder={isEmailForm ? "hello@email.com" : "+1 (123) 456-7890"}
            placeholderTextColor="text-typography-900"
            keyboardType={isEmailForm ? "email-address" : "phone-pad"}
            autoCapitalize="none"
            value={contact}
            onChangeText={(value) => {
              if (!isEmailForm) {
                setContact(formatPhoneNumber(value));
                onChange(getPhoneDigits(value), true);
              } else {
                setContact(value);
                onChange(value, false);
              }
            }}
            onFocus={() => {
              if (clearError) {
                clearError();
              }
            }}
          />
        </Input>
        {error && <Text className="text-error-100 text-[14px] leading-[20px]">{errorMessage}</Text>}
      </Box>
    </FormControl>
  );
};

export default ContactInput;
